import React, { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';

import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableFooter,
	TablePagination,
	TableRow,
	Typography,
	Box,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import UsersTableRow, { ROW_HEIGHT } from './UsersTableRow';
import UsersTableHead from './UsersTableHead';
import TablePaginationActions from './components/TablePaginationActions';
import Spinner from 'components/Spinner';
import SearchInput from 'common/components/SearchInput';
import ContainerBox from 'components/ContainerBox';

import { UserApi } from 'common/types';
import { useTranslation } from 'services/localization/useTranslation';
import { getUserAccessClaims, canEditUser, isAdmin } from 'common/modules/users/utils';
import {
	SortDirection,
	SortDirections,
	SortKey,
	SortKeys,
	UsersTableFilters,
	getFilteredUsers,
	getUsersToShow,
	getSortedUsers,
} from './utils';

import * as ShopSelectors from 'selectors/ShopSelectors';
import * as UserSelectors from 'selectors/UserSelectors';

const UsersTable = () => {
	const classes = useStyles();
	const { t } = useTranslation();

	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirections.asc);
	const [sortKey, setSortKey] = useState<SortKey>(SortKeys.email);
	const [filter, setFilter] = useState<UsersTableFilters>({
		roles: [],
		permissions: [],
		locations: [],
		search: '',
	});

	const activeShopId = useSelector(ShopSelectors.activeShopId)!;
	const activeUserAccess = useSelector(UserSelectors.userActiveShopAccess);
	const activeUser = useSelector(UserSelectors.userData)!;
	const activeUserId = activeUser?.id;
	const usersData = useSelector(ShopSelectors.usersData);
	const usersLoading = useSelector(ShopSelectors.usersLoading);
	const usersError = useSelector(ShopSelectors.usersError);
	const adminUsers = useSelector(ShopSelectors.adminUsers);
	const isOnlyOneAdmin = adminUsers.length === 1;

	const usersToShow = getUsersToShow({
		users: usersData,
		activeUser,
		shopId: activeShopId,
	});

	const filteredUsers = getFilteredUsers({
		users: usersToShow,
		filters: filter,
		shopId: activeShopId,
	});

	const sortedUsers = getSortedUsers({
		users: filteredUsers,
		sortDirection,
		sortKey,
	});

	const emptyRows =
		filteredUsers.length > rowsPerPage
			? rowsPerPage - Math.min(rowsPerPage, filteredUsers.length - page * rowsPerPage)
			: 0;
	const rowsToRender =
		rowsPerPage > 0
			? sortedUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
			: sortedUsers;

	const handleSort = (key: SortKey) => {
		const isAsc = sortKey === key && sortDirection === SortDirections.asc;
		setSortDirection(isAsc ? SortDirections.desc : SortDirections.asc);
		setSortKey(key);
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
	) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const renderLabelRowsPerPage = () => (
		<Typography variant="body2" color="secondary" component="span">
			{t('users.rowsPerPage', 'Rows per page')}:
		</Typography>
	);

	const renderUserRow = (user: UserApi) => {
		const accessClaims = getUserAccessClaims(user, activeShopId);
		if (!accessClaims || !activeUserAccess) return null;

		const isSelf = user.id === activeUserId;
		const canEdit = canEditUser(activeUserAccess, accessClaims);
		const isOnlyAdmin = isAdmin(accessClaims.roles) && isOnlyOneAdmin;
		return (
			<UsersTableRow
				key={user.id}
				user={user}
				accessClaims={accessClaims}
				disableDelete={isSelf || !canEdit}
				disableEdit={!canEdit}
				isOnlyAdmin={isOnlyAdmin}
				isSelf={isSelf}
			/>
		);
	};

	const handleSearchChange = useCallback((search) => {
		setFilter((prev) => ({ ...prev, search }));
	}, []);

	return (
		<>
			<Box className={classes.top}>
				<SearchInput
					className={classes.searchInput}
					searchValue={filter.search}
					t={t}
					delay={0}
					onChange={handleSearchChange}
					textFieldProps={{
						placeholder: t('users.searchByEmail', 'Search by email'),
					}}
				/>
				<Box p={1} />
			</Box>
			<ContainerBox padding="none">
				<TableContainer>
					<Table className={classes.table}>
						<UsersTableHead
							sortDirection={sortDirection}
							sortKey={sortKey}
							onSort={handleSort}
							filter={filter}
							onFilter={setFilter}
						/>
						<TableBody>
							{usersLoading ? (
								<TableRow className={classes.spinnerWrapper}>
									<TableCell colSpan={5}>
										<Spinner relative />
									</TableCell>
								</TableRow>
							) : usersError ? (
								<TableRow>
									<TableCell colSpan={5}>
										<Typography variant="body2" color="error" align="center">
											{t(
												'users.errorFetchingUsers',
												`An error occurred while fetching your shop's users. Please refresh the page to try again.`,
											)}
										</Typography>
									</TableCell>
								</TableRow>
							) : rowsToRender.length === 0 ? (
								<TableRow>
									<TableCell colSpan={5}>
										<Typography variant="body2" align="center">
											{t('common:states.noResults', 'No results')}
										</Typography>
									</TableCell>
								</TableRow>
							) : (
								<>
									{rowsToRender.map(renderUserRow)}
									{emptyRows > 0 && (
										<TableRow style={{ height: ROW_HEIGHT * emptyRows }}>
											<TableCell colSpan={5} />
										</TableRow>
									)}
								</>
							)}
						</TableBody>
						{usersToShow.length > 10 && (
							<TableFooter>
								<TableRow>
									<TablePagination
										rowsPerPageOptions={[
											10,
											20,
											30,
											{ label: t('common:count.all', 'All'), value: -1 },
										]}
										colSpan={5}
										count={filteredUsers.length}
										rowsPerPage={rowsPerPage}
										page={page}
										labelDisplayedRows={() => null}
										labelRowsPerPage={renderLabelRowsPerPage()}
										onPageChange={(_, newPage) => setPage(newPage)}
										onRowsPerPageChange={handleChangeRowsPerPage}
										ActionsComponent={TablePaginationActions}
										classes={{
											spacer: classes.firstSpacer,
											selectLabel: classes.middleSpacer,
											displayedRows: classes.middleSpacer,
										}}
									/>
								</TableRow>
							</TableFooter>
						)}
					</Table>
				</TableContainer>
			</ContainerBox>
		</>
	);
};

const useStyles = makeStyles((theme) =>
	createStyles({
		top: {
			display: 'flex',
			alignItems: 'flex-end',
			flexDirection: 'column-reverse',
			[theme.breakpoints.up('sm')]: {
				flexDirection: 'row',
				justifyContent: 'space-between',
				alignItems: 'flex-end',
			},
		},
		searchInput: {
			[theme.breakpoints.up('sm')]: {
				maxWidth: 300,
			},
		},
		table: {
			minWidth: 600,
		},
		firstSpacer: {
			flex: 0,
		},
		middleSpacer: {
			'&:empty': {
				flex: '1 1 100%',
			},
		},
		spinnerWrapper: {
			height: 100,
		},
	}),
);

export default UsersTable;
